import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '../use-api-backend-info';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useGetSiteConfigs } from '../use-site-configs';
import { ApiBackendInfo } from '../athena/types';
import { CwcJackpotWinningNotify } from './types';
import { AccountActStatus } from '@nf/utils-common/constants';
import { useAccountStatus } from '../account';
import { useState } from 'react';

const isMockEnabled = false;

/**
 * 取得 CwcJackpot 中獎資訊 (若有多期中獎，回傳最早那期)
 * @param apiBackendInfo 
 * @returns CwcJackpotWinningNotify
 */
const fetchCwcJackpotWinningNotify = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<CwcJackpotWinningNotify>> => {
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/CwcJackpot/GetWinningNotify`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

/**
 * 更新 CwcJackpot 派獎通知狀態
 * @param apiBackendInfo
 * @returns ApiResponse<void>
 */
const setCwcJackpotWinningNotify = async (apiBackendInfo: ApiBackendInfo): Promise<ApiResponse<void>> => {
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/CwcJackpot/SetWinningNotify`)(apiBackendInfo.token);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export const useCwcJackpotWinningNotify = () => {
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const apiBackendInfo = useApiBackendInfo();
	const [hasCheckNotify, setHasCheckNotify] = useState(false);
	const { data: actStatus } = useAccountStatus();

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [
			queryKeys.CWC_JACKPOT_WINNING_NOTIFY,
			apiBackendInfo.url,
			apiBackendInfo.token
		],
		queryFn: () => fetchCwcJackpotWinningNotify(apiBackendInfo),
		staleTime: Infinity,
		cacheTime: Infinity,
		retry: 5,
		retryDelay: 3000,
		enabled: Boolean(siteConfig?.EnableCwcJackpot) && actStatus === AccountActStatus.Deposited && apiBackendInfo.isReady && !hasCheckNotify,
		onSuccess: (response) => {
			if (response.Data) {
				setHasCheckNotify(true);
			}
		}
	});

	return {
		data: data?.Data,
		isLoading,
		isError,
		error,
		refetch,
		setCwcJackpotWinningNotify: () => setCwcJackpotWinningNotify(apiBackendInfo)
	};
}; 